import configuration from '@/taskpane/common/configuration'
import office from '@/taskpane/officejs/office-wrapper'
import state from '@/taskpane/state/sidebar-state'
import { defaultMessageArgs } from '../messages/message-args'
import sendMessageToSidebar from '../messages/send-message-to-sidebar'

const DIALOG_MAX_WIDTH = 1400
const DIALOG_MAX_HEIGHT = 900
const DIALOG_MIN_WIDTH = 1024
const DIALOG_MIN_HEIGHT = 600
const DIALOG_SCREEN_GUTTER = 48

// Owns the fullscreen Office Dialog (PENG-803): opening/closing it, the token handoff to
// the popout portal instance, and relaying between the dialog and the sidebar. The dialog
// page itself (dialog.html) is a dumb relay — every decision lives here.
class FullscreenDialogManager {
  private dialog: Office.Dialog | null = null
  private opening: boolean = false
  private closeRequestedWhileOpening: boolean = false

  isSupported(): boolean {
    // DialogApi 1.2 is required for messageChild — without it there is no token handoff
    // or host->popout forwarding, so those hosts stay on the narrow taskpane view
    return !office.platform.isMobile() && Office.context.requirements.isSetSupported('DialogApi', '1.2')
  }

  isOpen(): boolean {
    return this.dialog !== null
  }

  async open(path?: string): Promise<boolean> {
    if (this.dialog) return true
    if (this.opening) return false

    this.opening = true
    this.closeRequestedWhileOpening = false

    try {
      const dialogUrl = this.buildDialogUrl(this.buildPortalUrl(path))
      const dialogOptions: Office.DialogOptions = {
        width: this.sizePercentage(screen.width, DIALOG_MIN_WIDTH, DIALOG_MAX_WIDTH),
        height: this.sizePercentage(screen.height, DIALOG_MIN_HEIGHT, DIALOG_MAX_HEIGHT),
        displayInIframe: false,
        promptBeforeOpen: false,
      }

      const officeDialog = await office.ui.displayDialog(dialogUrl, dialogOptions)
      if (!officeDialog) {
        this.opening = false
        return false
      }

      officeDialog.addEventHandler(Office.EventType.DialogMessageReceived, this.onDialogMessage)
      officeDialog.addEventHandler(Office.EventType.DialogEventReceived, this.onDialogEvent)

      this.dialog = officeDialog
      this.opening = false

      if (this.closeRequestedWhileOpening) {
        this.close()
        return false
      }

      this.sendStatusToSidebar('opening')
      return true
    } catch {
      // Includes 12007: another dialog (e.g. login) is already open for this add-in
      this.opening = false
      return false
    }
  }

  close(path?: string) {
    if (this.opening) {
      this.closeRequestedWhileOpening = true
      return
    }
    if (!this.dialog) return

    try {
      this.dialog.close()
    } catch {
      // already gone — still notify so the sidebar overlay is dismissed
    }

    // DialogEventReceived 12006 does not fire for a programmatic close
    this.notifyClosed(path)
  }

  sendToDialog(messageType: string, args: any) {
    if (!this.dialog) return

    const message = { type: messageType, args: args }
    this.dialog.messageChild(JSON.stringify(message))
  }

  private onDialogMessage = (arg) => {
    // Office can deliver non-JSON payloads (diagnostics, future message shapes) — an
    // uncaught parse error here kills the handler and drops every later dialog message
    let message: { type: string; args?: any }
    try {
      message = JSON.parse(arg.message)
    } catch {
      return
    }

    switch (message.type) {
      case 'DialogReady':
        // Message channel is up; the dialog page is now loading the portal
        break
      case 'SidebarConnected':
        // The popout portal booted. A dialog window does not share the taskpane's storage
        // partition, so hand the session tokens over explicitly.
        this.sendToDialog('ClientConnected', {
          token: state.cirrusToken,
          refreshToken: state.cirrusRefreshToken,
          sendConnectedMessage: false,
          groupId: configuration.DIALOG_SOCKET_GROUP_ID,
        })
        break
      case 'SidebarLoginComplete':
        this.sendStatusToSidebar('ready')
        break
      case 'FullscreenViewClose':
        this.close(message.args && message.args.path)
        break
      case 'SidebarLoginRequested':
        // Token handoff failed and the popout wants a login dialog — Office allows one
        // dialog per add-in, so close the popout and let the narrow sidebar carry on
        console.log('Fullscreen popout requested login; closing the popout')
        this.close()
        break
      case 'Error':
        sendMessageToSidebar('Error', message.args)
        break
      default:
        // Extension point: popout-originated messages that need host services get a case
        // here. Everything else is dropped — the popout drives its own REST traffic.
        console.log('Fullscreen popout message dropped: ' + message.type)
        break
    }
  }

  private onDialogEvent = (arg) => {
    // 12006 = user closed the dialog; other codes (12002/12003) mean it never loaded.
    // Either way the popout is gone — release it and restore the sidebar.
    console.log('Fullscreen dialog event: ' + (arg && arg.error))

    // A programmatic close already released the dialog and notified the sidebar. Office
    // can still deliver 12006 afterwards, and notifying twice remounts the sidebar's
    // route tree a second time.
    if (!this.dialog) return

    this.notifyClosed()
  }

  private notifyClosed(path?: string) {
    this.dialog = null
    this.opening = false
    this.closeRequestedWhileOpening = false

    sendMessageToSidebar('SetView', { ...defaultMessageArgs(), view: 'sidebar' })
    this.sendStatusToSidebar('closed')

    if (path) {
      // The popout navigated off the fullscreen surface — land the sidebar there. Query is
      // stripped: the popout's socket params (groupId, popout) must not leak into the
      // sidebar, whose router re-appends its own on navigation.
      sendMessageToSidebar('RedirectSidebar', { ...defaultMessageArgs(), routePath: path.split('?')[0] })
    }
  }

  private sendStatusToSidebar(status: string) {
    sendMessageToSidebar('FullscreenViewStatus', { ...defaultMessageArgs(), status: status })
  }

  private buildPortalUrl(path?: string): string {
    const pathParts = (path || '/sidebar/mass-email/v2').split('?')
    const query = new URLSearchParams(pathParts[1] || '')

    query.set('socket', 'none')
    query.set('client', 'owa')
    query.set('groupId', configuration.DIALOG_SOCKET_GROUP_ID)
    query.set('popout', 'true')

    return configuration.CIRRUS_PORTAL + '/#' + pathParts[0] + '?' + query.toString()
  }

  private buildDialogUrl(portalUrl: string): string {
    let url = window.location.origin + '/dialog.html?portalUrl=' + encodeURIComponent(portalUrl)

    // The dialog bundle resolves CIRRUS_PORTAL through getPartnerUrl too, so white-label
    // taskpanes must carry the override across
    const partnerDomain = new URLSearchParams(window.location.search).get('partnerDomain')
    if (partnerDomain) {
      url += '&partnerDomain=' + encodeURIComponent(partnerDomain)
    }

    return url
  }

  // Office wants a percentage of the screen, so pick the pixel size first: fit inside the
  // screen minus a gutter, cap it at the maximum, hold the minimum, then never exceed the
  // screen itself (a screen smaller than the minimum just gets the whole screen).
  private sizePercentage(screenPixels: number, minPixels: number, maxPixels: number): number {
    const target = Math.min(Math.max(screenPixels - DIALOG_SCREEN_GUTTER, minPixels), maxPixels)
    const percentage = Math.round((100 * Math.min(target, screenPixels)) / Math.max(screenPixels, 1))

    return Math.min(100, Math.max(1, percentage))
  }
}

export default new FullscreenDialogManager()
